import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle, XCircle, Clock, Star, AlertCircle,
  ExternalLink, Download, UserPlus, X, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient } from '../services/api';

interface RatingScreenshot {
  uploadId: string;
  userId: string;
  platform: string;
  userRole: string;
  originalFileName: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  approvedRating: number | null;
  approvedReferenceText: string | null;
  rejectionReason: string | null;
  uploadedAt: string;
  reviewedAt: string | null;
  screenshotSasUrl: string | null;
  aiExtractedRating: number | null;
  assignedToUserId: string | null;
  assignedAt: string | null;
}

interface OpsUser {
  userId: string;
  fullName: string;
  email: string;
}

const STATUS_COLOURS: Record<string, string> = {
  Pending:  'bg-yellow-100 text-yellow-700',
  Approved: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
};

function StarDisplay({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-3 h-3 ${i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
      ))}
      <span className="ml-1 text-xs font-semibold text-gray-700">{rating.toFixed(1)}</span>
    </span>
  );
}

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          onMouseEnter={() => setHovered(i)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(i)}
          className="focus:outline-none"
        >
          <Star className={`w-5 h-5 transition-colors ${
            i <= (hovered || Math.round(value)) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'
          }`} />
        </button>
      ))}
      <input
        type="number" min={1} max={5} step={0.1} value={value}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        className="ml-2 w-14 text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );
}

function AssignModal({
  uploadId,
  onClose,
}: {
  uploadId: string;
  onClose: () => void;
}) {
  const { getToken } = useAppAuth();
  const client = createApiClient(getToken);
  const qc = useQueryClient();
  const [selected, setSelected] = useState('');

  const { data: opsUsers = [], isLoading } = useQuery<OpsUser[]>({
    queryKey: ['ops-users'],
    queryFn: () =>
      client.get<OpsUser[]>('/api/v1/identity/admin/users', { params: { role: 'Ops', pageSize: 100 } }).then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const assignMut = useMutation({
    mutationFn: () =>
      client.post(`/api/v1/ratings/screenshots/admin/${uploadId}/assign`, { assignedToUserId: selected }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-rating-screenshots'] }); onClose(); },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 text-sm">Assign for Portal Verification</h3>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-gray-500">
            Assign this screenshot to a team member who will verify the rating in the source portal.
          </p>
          {isLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {opsUsers.map(u => (
                <button
                  key={u.userId}
                  type="button"
                  onClick={() => setSelected(u.userId)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                    selected === u.userId
                      ? 'bg-blue-50 border border-blue-300 text-blue-800'
                      : 'border border-transparent hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0 text-xs font-semibold text-gray-600">
                    {u.fullName.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{u.fullName}</p>
                    <p className="text-xs text-gray-400 truncate">{u.email}</p>
                  </div>
                  {selected === u.userId && <CheckCircle className="w-4 h-4 text-blue-600 shrink-0 ml-auto" />}
                </button>
              ))}
              {opsUsers.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">No ops team members found.</p>
              )}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => assignMut.mutate()}
              disabled={!selected || assignMut.isPending}
              className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium py-2 rounded-lg transition-colors"
            >
              {assignMut.isPending
                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <UserPlus className="w-4 h-4" />
              }
              Assign
            </button>
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
          {assignMut.isError && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />Assignment failed. Please try again.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

type ExpandMode = 'approve' | 'reject' | null;

function ActionRow({
  item,
  mode,
  onApprove,
  onReject,
  processing,
  onCancel,
}: {
  item: RatingScreenshot;
  mode: ExpandMode;
  onApprove: (id: string, rating: number, text: string) => void;
  onReject:  (id: string, reason: string) => void;
  processing: boolean;
  onCancel: () => void;
}) {
  const [rating, setRating]   = useState<number>(item.aiExtractedRating ?? 0);
  const [refText, setRefText] = useState('');
  const [reason, setReason]   = useState('');

  if (!mode) return null;

  return (
    <tr>
      <td colSpan={9} className={`px-5 py-4 border-t ${
        mode === 'approve' ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'
      }`}>
        {mode === 'approve' && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-green-800">Approve — set verified rating</p>
            <div className="flex items-start gap-6 flex-wrap">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Rating</label>
                <StarPicker value={rating} onChange={setRating} />
              </div>
              <div className="flex-1 min-w-48">
                <label className="block text-xs text-gray-600 mb-1">Reference text (shown on public profile)</label>
                <input
                  type="text"
                  value={refText}
                  onChange={e => setRefText(e.target.value)}
                  placeholder={`e.g. "4.8/5 from 127 reviews on ${item.platform}"`}
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onApprove(item.uploadId, rating, refText)}
                disabled={processing}
                className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
              >
                {processing
                  ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <CheckCircle className="w-3.5 h-3.5" />
                }
                Approve & Publish
              </button>
              <button onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg border border-gray-200 hover:bg-white transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}

        {mode === 'reject' && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-red-800">Reject — enter reason for driver</p>
            <textarea
              rows={2}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Screenshot too blurry, rating not visible, wrong platform"
              className="w-full text-xs border border-red-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-400 focus:border-transparent resize-none bg-white"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => { if (!reason.trim()) return; onReject(item.uploadId, reason.trim()); }}
                disabled={!reason.trim() || processing}
                className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
              >
                {processing
                  ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <XCircle className="w-3.5 h-3.5" />
                }
                Confirm Rejection
              </button>
              <button onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg border border-gray-200 hover:bg-white transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}

function ageLabel(uploadedAt: string): string {
  const ms = Date.now() - new Date(uploadedAt).getTime();
  if (ms < 3_600_000)   return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000)  return `${Math.floor(ms / 3_600_000)}h ago`;
  return new Date(uploadedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
}

export default function AdminRatingReviewPage() {
  const { getToken, account } = useAppAuth();
  const client = createApiClient(getToken);
  const qc = useQueryClient();

  const [page, setPage]               = useState(1);
  const [expandRow, setExpandRow]     = useState<{ id: string; mode: ExpandMode } | null>(null);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [processing, setProcessing]   = useState<string | null>(null);

  const { data: items = [], isLoading } = useQuery<RatingScreenshot[]>({
    queryKey: ['admin-rating-screenshots', page],
    queryFn: () =>
      client.get<RatingScreenshot[]>('/api/v1/ratings/screenshots/admin/all', {
        params: { page, pageSize: 50 },
      }).then(r => r.data),
    enabled: !!account,
  });

  const toggleExpand = (id: string, mode: ExpandMode) => {
    setExpandRow(prev =>
      prev?.id === id && prev?.mode === mode ? null : { id, mode }
    );
  };

  const approve = async (id: string, rating: number, text: string) => {
    if (!rating || rating < 1 || rating > 5) { alert('Enter a rating between 1.0 and 5.0.'); return; }
    if (!text.trim())                         { alert('Reference text is required.'); return; }
    setProcessing(id);
    try {
      await client.post(`/api/v1/ratings/screenshots/admin/${id}/approve`, {
        approvedRating: rating, approvedReferenceText: text,
      });
      qc.invalidateQueries({ queryKey: ['admin-rating-screenshots'] });
      setExpandRow(null);
    } catch (e: any) {
      alert(e?.response?.data?.title ?? 'Approval failed.');
    } finally {
      setProcessing(null);
    }
  };

  const reject = async (id: string, reason: string) => {
    if (!reason.trim()) { alert('Enter a rejection reason first.'); return; }
    setProcessing(id);
    try {
      await client.post(`/api/v1/ratings/screenshots/admin/${id}/reject`, { rejectionReason: reason });
      qc.invalidateQueries({ queryKey: ['admin-rating-screenshots'] });
      setExpandRow(null);
    } catch (e: any) {
      alert(e?.response?.data?.title ?? 'Rejection failed.');
    } finally {
      setProcessing(null);
    }
  };

  const pending  = items.filter(i => i.status === 'Pending').length;
  const assigned = items.filter(i => i.assignedToUserId).length;

  return (
    <>
      {assigningId && (
        <AssignModal uploadId={assigningId} onClose={() => setAssigningId(null)} />
      )}

      <div className="page-shell">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">External Rating Screenshots</h1>
            <p className="text-sm text-gray-500 mt-1">
              Review uploaded screenshots, open or download attachments, and approve or reject ratings.
            </p>
          </div>
          <div className="flex gap-6 shrink-0">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{pending}</div>
              <div className="text-xs text-gray-400">pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{assigned}</div>
              <div className="text-xs text-gray-400">assigned</div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No screenshots on this page.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Screenshot</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Platform / Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">AI Rating</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Uploaded</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Assigned</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Actions</th>
                    <th className="px-3 py-3 w-8" />
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const isPending     = item.status === 'Pending';
                    const isProcessing  = processing === item.uploadId;
                    const currentMode   = expandRow?.id === item.uploadId ? expandRow.mode : null;

                    return (
                      <>
                        <tr key={item.uploadId} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">

                          {/* Screenshot image tile + open/download */}
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1.5">
                              <div className="w-20 h-20 rounded-xl overflow-hidden border border-gray-200 bg-gray-50 shadow-sm shrink-0">
                                {item.screenshotSasUrl ? (
                                  <a href={item.screenshotSasUrl} target="_blank" rel="noreferrer" title="Open full size">
                                    <img
                                      src={item.screenshotSasUrl}
                                      alt="screenshot"
                                      className="w-full h-full object-cover hover:opacity-80 transition-opacity"
                                    />
                                  </a>
                                ) : (
                                  <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                                    <Star className="w-5 h-5 text-gray-300" />
                                    <span className="text-[10px] text-gray-300">No image</span>
                                  </div>
                                )}
                              </div>
                              {item.screenshotSasUrl && (
                                <div className="flex gap-2">
                                  <a
                                    href={item.screenshotSasUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    <ExternalLink className="w-3 h-3" />Open
                                  </a>
                                  <a
                                    href={item.screenshotSasUrl}
                                    download={item.originalFileName}
                                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 hover:underline"
                                  >
                                    <Download className="w-3 h-3" />Save
                                  </a>
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Platform / Role */}
                          <td className="px-4 py-3">
                            <span className="font-semibold text-gray-900 text-sm">{item.platform}</span>
                            <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                              {item.userRole}
                            </span>
                            {item.status === 'Approved' && item.approvedReferenceText && (
                              <p className="text-xs text-green-600 italic mt-0.5 max-w-[200px] truncate">
                                "{item.approvedReferenceText}"
                              </p>
                            )}
                            {item.status === 'Rejected' && item.rejectionReason && (
                              <p className="text-xs text-red-500 mt-0.5 max-w-[200px] truncate">
                                {item.rejectionReason}
                              </p>
                            )}
                          </td>

                          {/* User */}
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs text-gray-500" title={item.userId}>
                              {item.userId.slice(0, 8)}…
                            </span>
                          </td>

                          {/* AI Rating */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.aiExtractedRating != null ? (
                              <div>
                                <StarDisplay rating={item.aiExtractedRating} />
                                <p className="text-xs text-gray-400 mt-0.5">AI detected</p>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>

                          {/* Uploaded */}
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                            {ageLabel(item.uploadedAt)}
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOURS[item.status]}`}>
                              {item.status === 'Pending'  && <Clock className="w-3 h-3" />}
                              {item.status === 'Approved' && <CheckCircle className="w-3 h-3" />}
                              {item.status === 'Rejected' && <XCircle className="w-3 h-3" />}
                              {item.status}
                            </span>
                            {item.status === 'Approved' && item.approvedRating != null && (
                              <div className="mt-1"><StarDisplay rating={item.approvedRating} /></div>
                            )}
                          </td>

                          {/* Assigned */}
                          <td className="px-4 py-3">
                            {item.assignedToUserId ? (
                              <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">
                                <UserPlus className="w-3 h-3" />Assigned
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {isPending ? (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleExpand(item.uploadId, 'approve')}
                                  disabled={isProcessing}
                                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                                    currentMode === 'approve'
                                      ? 'bg-green-100 text-green-800 border border-green-300'
                                      : 'bg-green-600 hover:bg-green-700 text-white'
                                  }`}
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  Approve
                                  {currentMode === 'approve'
                                    ? <ChevronUp className="w-3 h-3 ml-0.5" />
                                    : <ChevronDown className="w-3 h-3 ml-0.5" />
                                  }
                                </button>
                                <button
                                  onClick={() => toggleExpand(item.uploadId, 'reject')}
                                  disabled={isProcessing}
                                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                                    currentMode === 'reject'
                                      ? 'bg-red-100 text-red-800 border border-red-300'
                                      : 'bg-red-600 hover:bg-red-700 text-white'
                                  }`}
                                >
                                  <XCircle className="w-3.5 h-3.5" />
                                  Reject
                                  {currentMode === 'reject'
                                    ? <ChevronUp className="w-3 h-3 ml-0.5" />
                                    : <ChevronDown className="w-3 h-3 ml-0.5" />
                                  }
                                </button>
                                <button
                                  onClick={() => setAssigningId(item.uploadId)}
                                  title={item.assignedToUserId ? 'Re-assign' : 'Assign to team member'}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${
                                    item.assignedToUserId
                                      ? 'border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100'
                                      : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  <UserPlus className="w-3.5 h-3.5" />
                                  {item.assignedToUserId ? 'Re-assign' : 'Assign'}
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>

                          {/* Collapse indicator (shows when expanded) */}
                          <td className="px-3 py-3">
                            {currentMode && (
                              <button
                                onClick={() => setExpandRow(null)}
                                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                title="Collapse"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>

                        {/* Inline approve / reject panel */}
                        {currentMode && (
                          <ActionRow
                            key={`action-${item.uploadId}`}
                            item={item}
                            mode={currentMode}
                            onApprove={approve}
                            onReject={reject}
                            processing={isProcessing}
                            onCancel={() => setExpandRow(null)}
                          />
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && (items.length === 50 || page > 1) && (
          <div className="flex justify-center gap-3 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-600">Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={items.length < 50}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
}
